import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { deleteStudyAmount, fetchInventoryServices, fetchStudyAmounts, saveStudyAmount } from '../services/adminService';
import { fetchAgendaSpecialists, fetchAgendaSpecialties, filterSpecialistsBySpecialty } from '../services/agendaService';
import { getUserSession, isSuperAdmin } from '../services/authService';
import '../styles/admin.css';

const vacio = {
  especialidad: '',
  especialistaId: '',
  itemId: '',
  montoEspecialista: '',
  asociadoId: '',
  montoAsociado: '',
};

const AdminMontosEstudioPage = () => {
  const history = useHistory();
  const session = getUserSession();
  const sessionId = session ? session.id : null;
  const [especialidades, setEspecialidades] = useState([]);
  const [especialistas, setEspecialistas] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [montos, setMontos] = useState([]);
  const [form, setForm] = useState(vacio);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');

  const cargar = useCallback(() => {
    if (!sessionId) {
      return Promise.resolve();
    }

    setCargando(true);
    setError('');

    return Promise.all([
      fetchAgendaSpecialties(),
      fetchAgendaSpecialists(),
      fetchInventoryServices(sessionId),
      fetchStudyAmounts(sessionId),
    ])
      .then(([listaEspecialidades, listaEspecialistas, listaServicios, listaMontos]) => {
        setEspecialidades(listaEspecialidades);
        setEspecialistas(listaEspecialistas);
        setServicios(listaServicios.filter((servicio) => servicio.activo === true || servicio.activo === 1 || servicio.activo === '1'));
        setMontos(listaMontos);
      })
      .catch((requestError) => {
        setError(requestError.message || 'No se pudo cargar la configuración.');
      })
      .finally(() => {
        setCargando(false);
      });
  }, [sessionId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const especialistasDeLaEspecialidad = useMemo(
    () => filterSpecialistsBySpecialty(especialistas, form.especialidad),
    [especialistas, form.especialidad]
  );

  const itemsDeLaEspecialidad = useMemo(
    () =>
      servicios.filter(
        (servicio) => String(servicio.especialidad_codigo) === String(form.especialidad)
      ),
    [servicios, form.especialidad]
  );

  const asociados = useMemo(
    () => especialistas.filter((persona) => String(persona.id) !== String(form.especialistaId)),
    [especialistas, form.especialistaId]
  );

  const nombreEspecialidad = (codigo) => {
    const encontrada = especialidades.find(
      (item) => String(item.codigo) === String(codigo) || String(item.id) === String(codigo)
    );

    return encontrada ? encontrada.label : codigo;
  };

  const cambiar = (campo) => (event) => {
    const valor = event.target.value;

    setForm((actual) => {
      const siguiente = { ...actual, [campo]: valor };

      if (campo === 'especialidad') {
        siguiente.especialistaId = '';
        siguiente.itemId = '';
      }

      if (campo === 'especialistaId' && String(valor) === String(actual.asociadoId)) {
        siguiente.asociadoId = '';
        siguiente.montoAsociado = '';
      }

      if (campo === 'montoEspecialista' && valor.trim() === '') {
        siguiente.asociadoId = '';
        siguiente.montoAsociado = '';
      }

      if (campo === 'asociadoId' && valor === '') {
        siguiente.montoAsociado = '';
      }

      return siguiente;
    });
    setAviso('');
    setError('');
  };

  const limpiar = () => {
    setForm(vacio);
    setAviso('');
    setError('');
  };

  const guardar = () => {
    if (!form.especialidad) {
      setError('Selecciona la especialidad.');
      return;
    }

    if (!form.especialistaId) {
      setError('Selecciona el especialista.');
      return;
    }

    if (!form.itemId) {
      setError('Selecciona el ítem.');
      return;
    }

    if (form.montoEspecialista.trim() === '') {
      setError('Indica el monto del especialista.');
      return;
    }

    if (form.asociadoId && form.montoAsociado.trim() === '') {
      setError('Indica el monto del asociado.');
      return;
    }

    setGuardando(true);
    setError('');
    setAviso('');

    saveStudyAmount(sessionId, {
      especialista_usuario_id: Number(form.especialistaId),
      inventario_servicio_id: Number(form.itemId),
      monto_especialista: form.montoEspecialista.trim(),
      asociado_usuario_id: form.asociadoId ? Number(form.asociadoId) : 0,
      monto_asociado: form.asociadoId ? form.montoAsociado.trim() : '',
    })
      .then((lista) => {
        setMontos(lista);
        limpiar();
        setAviso('Monto guardado.');
      })
      .catch((requestError) => {
        setError(requestError.message || 'No se pudo guardar el monto.');
      })
      .finally(() => {
        setGuardando(false);
      });
  };

  const eliminar = (fila) => {
    if (!window.confirm('¿Eliminar este monto?')) {
      return;
    }

    setError('');
    setAviso('');

    deleteStudyAmount(sessionId, fila.id)
      .then((lista) => {
        setMontos(lista);
        setAviso('Monto eliminado.');
      })
      .catch((requestError) => {
        setError(requestError.message || 'No se pudo eliminar el monto.');
      });
  };

  const usarFila = (fila) => {
    setForm({
      especialidad: String(fila.especialidad_codigo),
      especialistaId: String(fila.especialista_usuario_id),
      itemId: String(fila.inventario_servicio_id),
      montoEspecialista: String(fila.monto_especialista),
      asociadoId: fila.asociado_usuario_id ? String(fila.asociado_usuario_id) : '',
      montoAsociado: fila.monto_asociado != null ? String(fila.monto_asociado) : '',
    });
    setAviso('');
    setError('');
  };

  if (!session || !isSuperAdmin(session)) {
    return null;
  }

  const puedeAsociado = form.montoEspecialista.trim() !== '';

  return (
    <div className="admin-root">
      <section className="admin-section">
        <header className="admin-header">
          <div>
            <h1>Montos por estudio</h1>
            <p>
              Lo que cobra el especialista por cada estudio. El asociado es opcional: hace actividades
              aparte con el paciente y su monto se usará en la nómina.
            </p>
          </div>
        </header>

        {error ? (
          <div className="admin-alert admin-alert--error" role="alert">
            {error}
          </div>
        ) : null}
        {aviso ? (
          <div className="admin-alert admin-alert--success" role="status">
            {aviso}
          </div>
        ) : null}

        {cargando ? <p className="admin-muted">Cargando…</p> : null}

        {!cargando ? (
          <>
            <div className="config-montos-principal">
              <label className="admin-field">
                <span>Especialidad</span>
                <select value={form.especialidad} onChange={cambiar('especialidad')}>
                  <option value="">Seleccione</option>
                  {especialidades.map((item) => (
                    <option key={item.codigo || item.id} value={item.codigo || item.id}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="admin-field">
                <span>Especialista</span>
                <select
                  value={form.especialistaId}
                  onChange={cambiar('especialistaId')}
                  disabled={!form.especialidad}
                >
                  <option value="">Seleccione</option>
                  {especialistasDeLaEspecialidad.map((persona) => (
                    <option key={persona.id} value={persona.id}>
                      {persona.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="admin-field">
                <span>Ítem</span>
                <select value={form.itemId} onChange={cambiar('itemId')} disabled={!form.especialidad}>
                  <option value="">Seleccione</option>
                  {itemsDeLaEspecialidad.map((servicio) => (
                    <option key={servicio.id} value={servicio.id}>
                      {servicio.nombre}
                    </option>
                  ))}
                </select>
              </label>
              <label className="admin-field">
                <span>Monto</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={form.montoEspecialista}
                  onChange={cambiar('montoEspecialista')}
                  placeholder="0.00"
                />
              </label>
            </div>

            <div className="config-montos-asociado">
              <label className="admin-field">
                <span>Asociado</span>
                <select
                  value={form.asociadoId}
                  onChange={cambiar('asociadoId')}
                  disabled={!puedeAsociado}
                >
                  <option value="">Sin asociado</option>
                  {asociados.map((persona) => (
                    <option key={persona.id} value={persona.id}>
                      {persona.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="admin-field">
                <span>Monto del asociado</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={form.montoAsociado}
                  onChange={cambiar('montoAsociado')}
                  placeholder="0.00"
                  disabled={!puedeAsociado || !form.asociadoId}
                />
              </label>
            </div>

            <div className="admin-pre-actions">
              <button type="button" className="admin-btn admin-btn--primary" onClick={guardar} disabled={guardando}>
                {guardando ? 'Guardando…' : 'Guardar'}
              </button>
              <button type="button" className="admin-btn admin-btn--secondary" onClick={limpiar}>
                Limpiar
              </button>
              <button
                type="button"
                className="admin-btn admin-btn--secondary"
                onClick={() => history.push('/admin/configuracion')}
              >
                Salir
              </button>
            </div>

            <h2>Costo de estudios por ítem</h2>
            {montos.length === 0 ? <p className="admin-muted">Todavía no hay montos guardados.</p> : null}
            {montos.length > 0 ? (
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Especialista</th>
                      <th>Especialidad</th>
                      <th>Ítem</th>
                      <th>Monto</th>
                      <th>Asociado</th>
                      <th>Monto</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {montos.map((fila) => (
                      <tr key={fila.id} className="config-monto-fila" onClick={() => usarFila(fila)}>
                        <td>{fila.especialista_nombre}</td>
                        <td>{nombreEspecialidad(fila.especialidad_codigo)}</td>
                        <td>{fila.item_nombre}</td>
                        <td>{fila.monto_especialista}</td>
                        <td>{fila.asociado_nombre || '—'}</td>
                        <td>{fila.monto_asociado != null ? fila.monto_asociado : '—'}</td>
                        <td>
                          <button
                            type="button"
                            className="admin-btn admin-btn--danger"
                            onClick={(event) => {
                              event.stopPropagation();
                              eliminar(fila);
                            }}
                          >
                            Eliminar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </>
        ) : null}
      </section>
    </div>
  );
};

export default AdminMontosEstudioPage;
